<script setup>
import {computed, inject, onMounted, provide, reactive, ref} from "vue";
//import functions

//import components
import {Icon} from "@iconify/vue";
import {Dialog, DataTable, Column, FloatLabel, InputText} from "primevue";
//import stores
//import composables
import {useBreakpoint} from "@/composables/useBreakpoint.js";
import SortIcon from "@/components/datatable/sortIcon.vue";
import {useUserStore} from "@/stores/user.js";
import {showMessage} from "@/utils/showMessage.js";
import {usePaymentMethodsStore} from "@/stores/payment.js";
import {useLoadingFlags} from "@/composables/useLoadingFlags.js";
import {useConfirm} from "primevue/useconfirm";
import Popover from "@/views/payment/Popover.vue";
import UserProfile from "@/components/UserProfile/index.vue";
import {required} from "@vuelidate/validators";
import {useVuelidate} from "@vuelidate/core";

//stores
const userStore = useUserStore();
const paymentMethodsStore = usePaymentMethodsStore();

//loading flags
const {
  fetching,
  setFetching,
} = useLoadingFlags()

//other composables
const {isXL} = useBreakpoint();
const confirm = useConfirm();
//emits

//props
const props = defineProps({
  orders: {
    type: Array,
    required: true
  },
  taker: {
    type: Object,
    required: true
  }
})

//injects
const visible = inject('isShowPaymentModal')

//component variables
const popover = ref()
const loading = ref(false)
const user = ref(null)
const cryptoPaymentMethod = ref(null)
const cryptoAddress = reactive({
  "network": 'USDT BEP20',
  "address": ''
})
const selectedTaker = ref(null)
const isShowUserProfileModal = ref(false);
const totalValue = props.orders.reduce((sum, order) => {
  const value = parseFloat(order?.value)

  return sum + (isNaN(value) ? 0 : value)
}, 0).toFixed(2)

const rules = computed(() => {
  return {
    address: {required}
  };
});
const $vuelidate = useVuelidate(rules, cryptoAddress);

//component functions

const getUserData = async () => {
  loading.value = true
  const res = await userStore.action_getCompleteProfile3({id: props.taker.takerID})
  if (res.result === true) {
    user.value = res.data
  } else {
    showMessage(res, 'error')
  }
  loading.value = false
}

const getPaymentMethods = async () => {
  setFetching(true)
  const res = await paymentMethodsStore.action_getPaymentMethods2({id: props.taker.takerID});
  if (res.result === true) {
    cryptoPaymentMethod.value = res.data.find(m => m.type === 'Crypto')
    cryptoAddress.address = cryptoPaymentMethod.value?.crypto?.address ? cryptoPaymentMethod.value?.crypto?.address : ''
  } else {
    showMessage(res, 'error')
  }
  setFetching(false)
};

const pay = async () => {
  console.log(cryptoAddress)
  $vuelidate.value.$touch();
  if ($vuelidate.value.$invalid) {
    return false;
  }
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `You are paying $1,000 to ${props.taker.takerName} at address \n ${cryptoAddress.value}`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Delete',
      size: 'small'
    },
    accept: async () => {
      visible.value = false
      /*try {
        const res = await ordersStore.action_removeHotOrder(props.orderType, {id: data.id})
        if (res.result === true) {
          //toast.add({severity: 'success', summary: 'Successful', detail: 'Taker Deleted', life: 3000});
          showMessage('Taker Deleted', 'success');

          emit('refreshOrder')
          //await getHotOrders();
        } else {
          //toast.add({severity: 'error', summary: 'Failed', detail: res, life: 3000});
          showMessage(res, 'error');
        }
      } catch (err) {
        //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 3000});
        showMessage(err, 'error');
      } finally {
      }*/
    },
    reject: () => {
      return
    }
  });
}

function showUserProfileModal(user) {
  selectedTaker.value = user
  isShowUserProfileModal.value = true
}

//hooks
onMounted(() => {
  getUserData()
  getPaymentMethods();
})

provide('isShowUserProfileModal', isShowUserProfileModal)
</script>

<template>
  <Dialog v-model:visible="visible"
          :class="['max-w-3xl !border-0 !rounded-md !bg-[#0e1726]', isXL ? '' : 'w-full']"
          :position="isXL ? 'center' : 'bottom'"
          closable dismissable-mask draggable modal
          content-class="min-w-full min-h-full flex flex-col gap-4 overflow-hidden h-full w-full max-w-[calc(100vw-20px)] max-h-[calc(100vh-100px)]">
    <template #header>
      <div class="text-base py-3">
        Payment Of Orders
      </div>
    </template>
    <template #closebutton="{closeCallback}">
      <div class="h-full w-[1px] bg-slate-700"></div>
      <button type="button" class="text-white-dark outline-none hover:text-red-500" @click="closeCallback">
        <Icon icon="pajamas:close-xs"/>
      </button>
    </template>
    <div class="flex-1 flex flex-col gap-4 overflow-hidden pt-4 pb-1 text-xs 2xl:text-sm min-w-80 max-w-full h-full">
      <div v-if="loading"
           class="flex items-center gap-2 text-sm leading-[1] border border-slate-700 bg-slate-800 px-2 py-2 rounded-md animate-pulse h-14">

      </div>
      <div v-else class="flex items-center gap-2 text-sm leading-[1] border border-slate-700 px-2 py-2 rounded-md">
        <div @click.stop="showUserProfileModal(user)" class="cursor-zoom-in">
          <img v-if="user.discordAvatar" :alt="user.name"
               :src="user.discordAvatar"
               class="size-10 rounded-full border border-slate-700"/>
          <div v-else
               class="flex items-center justify-center size-10 rounded-full border border-slate-700">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                 class="lucide lucide-user-icon lucide-user">
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/>
              <circle cx="12" cy="7" r="4"/>
            </svg>
          </div>
        </div>
        <div @click.stop class="flex flex-col gap-0.5">
          <div class="flex flex-row gap-1 items-center">
            <div class="select-text cursor-text">{{ user.name }}</div>
            <div v-if="user.rating" class="flex flex-row items-center gap-0.5">
                        <span class="flex flex-row items-end">
                          {{ user.rating }}⭐ ({{ user.ratingCount }})
                        </span>
            </div>
            <span v-if="user.guildTagBoost">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M5 13L16.5 1.5L12.5 10H20L5.5 23L12.5 13H5Z" fill="#FCA500" stroke="#F88200"
                                stroke-width="0.25"/>
                          <path d="M14 12L9.5 18.5L17 11H10.5L14 5L7.5 12H14Z" fill="#FDFDDB" stroke="#FFE430"
                                stroke-width="0.25"/>
                        </svg>
                      </span>
          </div>
          <div class="flex flex-col w-max text-slate-400 select-text cursor-text">
            <div>
              {{
                user.discordUsername ? user.discordUsername : "ـــــ"
              }}
              -
              {{ user.discordDisplayName ? user.discordDisplayName : "ـــــ" }}
            </div>
          </div>
        </div>
      </div>
      <DataTable :nullSortOrder="-1"
                 :value="props.orders"
                 scrollable
                 :scrollHeight="`calc(100vh - 450px)`"
                 resizableColumns
                 removableSort
                 stripedRows
                 class="text-xs w-full"
                 size="small"
                 tableStyle="height: 100%; width: 100%; table-layout: fixed;"
                 row-hover
                 dataKey="id"
                 :rowClass="() => 'hover:!bg-[#2c4967] even:!bg-[#1A2941] odd:!bg-[#121E31] !text-slate-300'"
      >
        <Column field="uuid" header="UUID" :sortable="true" style="width: 10%; height: 30px; min-width: 77px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
            <span @click.stop>{{ slotProps.data.uuid }}</span>
          </template>
        </Column>
        <Column field="title" header="Title" :sortable="true" style="width: 10%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="notes" header="Notes" :sortable="true" style="width: 10%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
          <template #body="slotProps">
                  <span @click.stop
                        @mouseover="popover.displayPopover($event, slotProps.data.notes)"
                        @mouseleave="popover.hidePopover()">
                   {{ slotProps.data.notes }}
                  </span>
          </template>
        </Column>
        <Column field="amount" header="Amount" :sortable="true" style="width: 8%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <Column field="currency" header="Currency" :sortable="true" style="width: 8%; height: 30px"
                class="overflow-hidden text-ellipsis whitespace-nowrap !py-0 cursor-pointer">
          <template #sorticon="{ sortOrder }">
            <SortIcon :sortOrder="sortOrder"/>
          </template>
        </Column>
        <template #empty>
          <h1 class="py-2 text-center text-lg">No Data</h1>
        </template>
      </DataTable>
      <Popover ref="popover"/>
      <div v-if="loading"
           class="flex items-center gap-2 text-sm leading-[1] border border-slate-700 bg-slate-800 px-2 py-2 rounded-md animate-pulse h-32">

      </div>
      <template v-else>
        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div class="col-span-2 flex flex-row gap-4 items-center">
            <div class="flex-1 flex flex-col">
              <span class=" text-xs" :class="[$vuelidate.address.$error ? 'text-red-300' : 'text-slate-400']">Address ({{ cryptoAddress.network }})</span>
              <div class="flex flex-col gap-0.5 text-xs 2xl:text-sm w-full">
                <InputText type="text" v-model="cryptoAddress.address" autocomplete="off"
                           class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm "
                           :class="[$vuelidate.address.$error ? '!border-red-300' : '!border-slate-700']"/>
                <template v-if="$vuelidate.address.$error">
                  <p class="text-red-600 dark:text-red-300 mt-1 text-xs"
                     v-if="!$vuelidate.address.required.$response">
                    This field is required
                  </p>
                </template>
              </div>
            </div>
          </div>
          <div class="col-span-2 flex flex-row gap-4 items-center justify-between border-t border-slate-700 pt-4">
            <div class="flex flex-col">
              <span class="text-slate-400 text-xs">Total Value</span>
              <span>{{ totalValue }} $</span>
            </div>

            <button @click="pay"
                    class="flex items-center gap-2 border border-slate-400 text-slate-400 cursor-pointer px-4 py-1 rounded-md hover:bg-slate-400 hover:text-slate-950 self-end">
              <svg xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24">
                <path d="M0 0h24v24H0z" fill="none"/>
                <path fill="currentColor"
                      d="M23.8 3a1.5 1.5 0 0 0-1.5-1.5h-14A1.5 1.5 0 0 0 6.8 3v8a1.5 1.5 0 0 0 1.5 1.5h14a1.5 1.5 0 0 0 1.5-1.5Zm-2 7.25a.25.25 0 0 1-.25.25H9.05a.25.25 0 0 1-.25-.25V3.78a.25.25 0 0 1 .25-.25h12.5a.25.25 0 0 1 .25.25Z"/>
                <path fill="currentColor"
                      d="M13.3 7.03a2 2 0 1 0 4 0a2 2 0 1 0-4 0m6.9 9.5l-3.53 1.17a.23.23 0 0 0-.15.14A2 2 0 0 1 14.7 19h-4a.5.5 0 0 1-.5-.5a.5.5 0 0 1 .5-.5h4a1 1 0 0 0 0-2h-3.5a7.15 7.15 0 0 0-4-1.5H5.14a4 4 0 0 0-1.79.5l-3 1.51a.25.25 0 0 0-.13.22v5.4a.26.26 0 0 0 .13.22a.25.25 0 0 0 .25 0l3.22-2a1 1 0 0 1 .85-.1c10 3.36 6.63 3.37 17.87-2.31a.52.52 0 0 0 .06-.94a2.48 2.48 0 0 0-2.4-.47"/>
              </svg>
              Pay
            </button>
          </div>
        </div>
      </template>
    </div>
  </Dialog>
  <UserProfile v-if="isShowUserProfileModal" :userID="selectedTaker.id"/>
</template>
<style>

</style>